"use client";
import { Swiper, SwiperSlide } from "swiper/react";
import {
  Autoplay,
  EffectCoverflow,
  Navigation,
  Pagination,
} from "swiper/modules";

import styles from "./hero.module.css";

import { useState } from "react";
import { Button } from "antd";
import { IconArrowNarrowLeft, IconArrowNarrowRight } from "@/lib/icons";

import Slide from "./slide";
import CMSLink from "../common/CMSLink";
import CMSTextBlock from "../common/CMSTextBlock";
import Container from "../container";
import ConvertedTime from "../convertedtime";

const resolveColorRgb = (colorValue) => {
  if (!colorValue) return null;
  if (typeof colorValue === "string") {
    const hex = colorValue.trim().replace(/^#/, "");
    const full =
      hex.length === 3
        ? hex
            .split("")
            .map((c) => c + c)
            .join("")
        : hex;
    if (!/^[0-9a-f]{6}$/i.test(full)) return null;
    const num = parseInt(full, 16);
    return { r: (num >> 16) & 255, g: (num >> 8) & 255, b: num & 255 };
  }
  if (colorValue?.metaColor?.isValid) {
    const { r, g, b } = colorValue.metaColor;
    return { r, g, b };
  }
  return null;
};

/** Single CMS slide — image card on the left, event info on the right */
function CmsSlide({ slide, isPriority }) {
  const {
    image,
    mobileImage,
    alt = "Hero Event Slide",
    bannerHasText,
    headingLines,
    subheadingLines,
    textLayout,
    title,
    subheading,
    tag,
    chipColor,
    date,
    location,
    price,
    price_tag,
    amount,
    urlConfig,
    backgroundColor: cmsBgColor,
    background_color,
    displayPrice,
    start_date,
    start_time,
    end_date,
    end_time,
    timezone,
  } = slide;
  const backgroundColor = cmsBgColor || background_color;

  const hasStructuredText =
    (Array.isArray(headingLines) && headingLines.length > 0) ||
    (Array.isArray(subheadingLines) && subheadingLines.length > 0);

  const picture = image ? (
    <picture>
      {mobileImage && (
        <source media="(max-width: 767px)" srcSet={mobileImage} width={767} height={500} />
      )}
      <img
        src={image}
        alt={alt}
        width={1440}
        height={450}
        fetchPriority={isPriority ? "high" : "low"}
        loading={isPriority ? "eager" : "lazy"}
        decoding="async"
        className={`w-full object-cover ${styles.cmsSlideImg}`}
      />
    </picture>
  ) : null;

  // No text for this slide — pure image, full width, nothing overlaid.
  if (!bannerHasText) {
    return (
      <CMSLink
        config={urlConfig}
        fallbackWrapper
        className={styles.cmsSlideLink}
      >
        <div
          className={styles.cmsSlide}
          style={backgroundColor ? { backgroundColor } : undefined}
        >
          {picture}
        </div>
      </CMSLink>
    );
  }

  return (
    <div className={styles.cmsSlideCard}>
      {picture && (
        <CMSLink
          config={urlConfig}
          fallbackWrapper
          className={styles.cmsSlideImageCol}
        >
          {picture}
        </CMSLink>
      )}

      <div className={styles.cmsSlideContent}>
        <div className={styles.cmsSlideTopGroup}>
          {tag?.length > 0 && (
            <div className={styles.cmsTagRow}>
              {tag.map((t, i) => (
                <span
                  key={i}
                  className={styles.cmsTag}
                  style={chipColor ? { backgroundColor: chipColor } : undefined}
                >
                  {t}
                </span>
              ))}
            </div>
          )}

          {(hasStructuredText || title || subheading) && (
            <CMSTextBlock
              headingLines={headingLines}
              subheadingLines={subheadingLines}
              heading={title}
              subheading={subheading}
              titleTag="h2"
              textLayout={textLayout}
              maxWidth={textLayout?.maxWidth || "full"}
              splitHeadingFallbackByComma
              className={styles.cmsTextBlock}
              headingClassName={styles.cmsTextHeading}
              subheadingClassName={styles.cmsTextSubheading}
              defaultHeadingColor="#021526"
              defaultSubheadingColor="#4a5568"
            />
          )}

          {(date || location || start_date) && (
            <div className={styles.cmsSlideMetaRow}>
              {location && (
                <span className={styles.cmsSlideMeta}>{location}</span>
              )}
              {location && (date || start_date) && (
                <span className={styles.metaDivider}>|</span>
              )}
              {start_date ? (
                <span className={styles.cmsSlideMeta}>
                  <ConvertedTime
                    time={start_time}
                    date={start_date}
                    timezone={timezone}
                  />
                </span>
              ) : date ? (
                <span className={styles.cmsSlideMeta}>{date}</span>
              ) : null}
            </div>
          )}

          {(displayPrice !== undefined ||
            amount !== undefined ||
            price !== undefined ||
            price_tag !== undefined ||
            "299") && (
            <div className={styles.cmsPriceTag}>
              {(() => {
                const finalPrice =
                  [displayPrice, amount, price, price_tag].find(
                    (p) => p !== undefined && p !== null && p !== "",
                  ) ?? "299";
                return finalPrice === 0 ||
                  finalPrice === "0" ||
                  finalPrice === "0.00"
                  ? "Free"
                  : `₹${finalPrice}`;
              })()}
            </div>
          )}
        </div>

        <div className={styles.cmsSlideBottomGroup}>
          <div className={styles.cmsCtaRow}>
            <CMSLink config={urlConfig} className={styles.cmsCta}>
              {urlConfig?.label || "Book ticket"}
            </CMSLink>
          </div>
        </div>
      </div>
    </div>
  );
}

const resolveImageUrl = (image) => {
  if (!image) return null;
  if (typeof image === "string") return image;
  if (Array.isArray(image) && image.length > 0) {
    const item = image[0];
    if (typeof item === "string") return item;
    return item?.url || item?.src || item?.thumbnail || null;
  }
  if (typeof image === "object") {
    return image.url || image.src || image.thumbnail || null;
  }
  return null;
};

export default function HeroSlide({ slides: initialSlides, cms }) {
  const [activeIndex, setActiveIndex] = useState(0);
  // State, not useRef — a ref's .current is still null on the render that
  // builds Swiper's navigation config, so the prev/next buttons silently
  // didn't work until some later re-render happened to fire. Passing these
  // as callback refs (`ref={setPrevEl}`) triggers a re-render as soon as
  // the DOM nodes exist, so navigation is wired up correctly from the start.
  const [prevEl, setPrevEl] = useState(null);
  const [nextEl, setNextEl] = useState(null);

  const slides = initialSlides?.filter((slide) => {
    const slideDate = slide.start_date || slide.date;
    if (!slideDate) return true;

    let eventDate;
    if (slide.start_date && slide.start_time) {
      eventDate = new Date(`${slide.start_date}T${slide.start_time}`);
      if (isNaN(eventDate.getTime())) {
        eventDate = new Date(`${slide.start_date} ${slide.start_time}`);
      }
    } else {
      eventDate = new Date(slideDate);
      eventDate.setHours(23, 59, 59, 999);
    }

    if (isNaN(eventDate.getTime())) return true;
    return eventDate.getTime() >= Date.now();
  });

  if (!slides || !Array.isArray(slides) || slides.length === 0) {
    return null;
  }

  if (cms) {
    const currentSlide = slides?.[activeIndex] || slides?.[0];
    const bgImageUrl = resolveImageUrl(currentSlide?.image);

    return (
      <section className={styles.cmsSectionBannerBg} suppressHydrationWarning>
        {/* Full background banner image blurred 70% */}
        {bgImageUrl && (
          <div className={styles.bgBannerBlurWrapper}>
            <img
              key={bgImageUrl}
              src={bgImageUrl}
              alt=""
              loading="lazy"
              decoding="async"
              className={styles.bgBannerBlurImg}
            />
            <div className={styles.bgBannerOverlay} />
          </div>
        )}

        <Container style={{ position: "relative", zIndex: 2 }}>
          <div className={`${styles.hero} ${styles.heroCms}`}>
            {slides?.length > 1 && (
              <>
                <Button
                  ref={setPrevEl}
                  shape="circle"
                  icon={<IconArrowNarrowLeft color="#021526" size={20} />}
                  className={`${styles.navBtn} ${styles.navPrev}`}
                  variant="filled"
                  color="default"
                  aria-label="Previous slide"
                />
                <Button
                  ref={setNextEl}
                  shape="circle"
                  icon={<IconArrowNarrowRight color="#021526" size={20} />}
                  className={`${styles.navBtn} ${styles.navNext}`}
                  variant="filled"
                  color="default"
                  aria-label="Next slide"
                />
              </>
            )}

            <Swiper
              modules={[Autoplay, Pagination, Navigation]}
              slidesPerView={1}
              loop={slides?.length > 1}
              autoplay={slides?.length > 1 ? { delay: 4000 } : false}
              navigation={{
                prevEl,
                nextEl,
              }}
              pagination={{
                clickable: true,
                el: ".hero-pagination",
              }}
              onSlideChange={(swiper) => setActiveIndex(swiper.realIndex)}
            >
              {slides?.map((slide, index) => (
                <SwiperSlide key={index}>
                  <CmsSlide slide={slide} isPriority={index === 0} />
                </SwiperSlide>
              ))}
            </Swiper>
            <div className="hero-pagination"></div>
          </div>
        </Container>
      </section>
    );
  }

  return (
    <div className={`${styles.hero}`} suppressHydrationWarning>
      <Swiper
        modules={[Autoplay, Pagination, EffectCoverflow]}
        slidesPerView={1.3}
        centeredSlides
        initialSlide={1}
        pagination={{
          type: "bullets",
          clickable: true,
          dynamicBullets: true,
          el: ".hero-pagination",
        }}
        loop={slides?.length > 2}
        effect="coverflow"
        coverflowEffect={{
          rotate: 50,
          stretch: 0,
          depth: 100,
          modifier: 1,
          slideShadows: true,
        }}
        breakpoints={{
          768: { slidesPerView: 1.2 },
          992: { slidesPerView: 1.2, spaceBetween: 24 },
          1490: { slidesPerView: 1.5, spaceBetween: 80 },
        }}
      >
        {slides?.map((slide, index) => (
          <SwiperSlide key={index}>
            <Slide {...slide} isPriority={index === 0} />
          </SwiperSlide>
        ))}
      </Swiper>
      <div className="hero-pagination"></div>
    </div>
  );
}
